/**
 * WholesaleProtectedRoute - guards all /wholesale routes.
 *
 * Requirements:
 * 1. User must be authenticated.
 * 2. User must have customerType === 'wholesale'.
 * 3. User must have an approved wholesale account (checked via account API).
 *
 * If authenticated but not approved, shows a "pending" message rather than
 * redirecting to a dead end.
 */

import { useState, useEffect } from 'react'
import { Navigate } from 'react-router'
import type { ReactNode } from 'react'
import { useAuthContext } from '../auth/useAuthContext.js'
import { getWholesaleAccount } from './wholesale-api.js'

interface WholesaleProtectedRouteProps {
  children: ReactNode
}

type AccountStatus = 'loading' | 'approved' | 'pending' | 'not_wholesale' | 'unauthenticated'

export function WholesaleProtectedRoute({ children }: WholesaleProtectedRouteProps) {
  const { user, loading: authLoading } = useAuthContext()
  const [status, setStatus] = useState<AccountStatus>('loading')

  useEffect(() => {
    if (authLoading) return

    let cancelled = false

    const run = async () => {
      if (!user) {
        if (!cancelled) setStatus('unauthenticated')
        return
      }
      if (user.customerType !== 'wholesale') {
        if (!cancelled) setStatus('not_wholesale')
        return
      }
      // Check account approval status
      try {
        const res = await getWholesaleAccount()
        if (cancelled) return
        if (res.error || !res.data) {
          setStatus('pending')
        } else if (res.data.approved) {
          setStatus('approved')
        } else {
          setStatus('pending')
        }
      } catch {
        if (!cancelled) setStatus('pending')
      }
    }

    run()

    return () => { cancelled = true }
  }, [user, authLoading])

  if (authLoading || status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-current border-t-transparent"
          style={{ color: 'var(--color-primary)' }}
        />
      </div>
    )
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login?next=/wholesale" replace />
  }

  if (status === 'not_wholesale') {
    return <Navigate to="/" replace />
  }

  if (status === 'pending') {
    return <WholesalePendingMessage />
  }

  return <>{children}</>
}

function WholesalePendingMessage() {
  return (
    <div
      className="flex min-h-screen items-center justify-center p-8"
      style={{ backgroundColor: 'var(--color-surface)' }}
    >
      <div
        className="max-w-lg w-full rounded-2xl p-10 text-center"
        style={{
          backgroundColor: 'var(--color-background)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--color-amber-50)' }}
        >
          <svg
            className="h-8 w-8"
            style={{ color: 'var(--color-amber-600)' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1
          className="mb-3 text-2xl font-bold"
          style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-display)' }}
        >
          Application Under Review
        </h1>
        <p className="text-base" style={{ color: 'var(--color-text-secondary)' }}>
          Your wholesale account application is pending approval. We typically review
          applications within 1-2 business days. You will receive an email once
          your account is approved.
        </p>
        <p className="mt-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Questions? Contact us at{' '}
          <a
            href="mailto:wholesale@provendoro.com"
            className="underline"
            style={{ color: 'var(--color-primary)' }}
          >
            wholesale@provendoro.com
          </a>
        </p>
      </div>
    </div>
  )
}
